import type { ConditionOp, FieldDef, IqDerivation, LookupDef } from '@jfh/intake-shared';

/** A lookup list key made from its name: "Sales reps" → "sales_reps". */
export function keyFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

/** One column of a sampled iQ record. `nested` columns can only be kept as extras. */
export interface SampleColumn {
  path: string;
  sample: string;
  nested: boolean;
}

function show(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return Array.isArray(v) ? `[${v.length} items]` : '{…}';
  return String(v);
}

/**
 * The columns of sampled records, in first-seen order: top-level values, plus
 * one level into nested objects (e.g. `PostingMap.Code`). The sample text is
 * the first non-empty value seen.
 */
export function columnsOf(records: unknown[]): SampleColumn[] {
  const cols = new Map<string, SampleColumn>();
  const add = (path: string, v: unknown, nested: boolean) => {
    const cur = cols.get(path);
    const text = show(v);
    if (!cur) cols.set(path, { path, sample: text, nested });
    else if (!cur.sample && text) cur.sample = text;
  };
  for (const r of records) {
    if (!r || typeof r !== 'object' || Array.isArray(r)) continue;
    for (const [k, v] of Object.entries(r as Record<string, unknown>)) {
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) {
          if (v2 === null || typeof v2 !== 'object') add(`${k}.${k2}`, v2, true);
        }
      } else if (!Array.isArray(v)) {
        add(k, v, false);
      }
    }
  }
  return [...cols.values()];
}

/** A name for a kept-too column, as rules and iQ mappings refer to it. */
export function extraNameFor(path: string): string {
  const parts = path.split('.');
  return parts.length === 1
    ? parts[0]!
    : parts.map((p, i) => (i === 0 ? p.charAt(0).toLowerCase() + p.slice(1) : p.charAt(0).toUpperCase() + p.slice(1))).join('');
}

/** Toggle a column in a list's kept-too extras. */
export function toggleExtra(def: LookupDef, path: string): LookupDef {
  const extras = def.extraFields ?? [];
  const has = extras.some((e) => e.path === path);
  return {
    ...def,
    extraFields: has ? extras.filter((e) => e.path !== path) : [...extras, { name: extraNameFor(path), path }],
  };
}

/** A worked-out iQ value as sentences, e.g. "When DefaultCurrency.Code is GBP, set PostingMap.Code to GBP." */
export function derivationSentences(d: IqDerivation): string[] {
  const words: Record<ConditionOp, string> = {
    eq: 'is',
    neq: 'isn’t',
    contains: 'contains',
    in: 'is one of',
    gt: 'is more than',
    lt: 'is less than',
    empty: 'is blank',
    not_empty: 'has a value',
  };
  const op = (o?: ConditionOp) => words[o ?? 'eq'];
  const out = d.rules.map((r) =>
    ['empty', 'not_empty'].includes(r.op ?? 'eq')
      ? `When ${d.sourcePath} ${op(r.op)}, set ${d.targetPath} to “${r.value}”.`
      : `When ${d.sourcePath} ${op(r.op)} “${r.equals}”, set ${d.targetPath} to “${r.value}”.`,
  );
  if (d.fallback) out.push(`Otherwise set ${d.targetPath} to “${d.fallback}”.`);
  if (!out.length) out.push(`Nothing yet: add a rule to fill ${d.targetPath || 'the iQ field'} from ${d.sourcePath || 'another field'}.`);
  return out;
}

/** The iQ test result in plain words. */
export function plainIqResult(
  r: { ok: boolean; status: number | null; error: string | null; gotHeaders: boolean; configured: boolean; url: string },
  ms: number,
): { ok: boolean; text: string } {
  const secs = (ms / 1000).toFixed(2);
  if (!r.url || (!r.configured && r.error === 'No base URL configured')) {
    return { ok: false, text: 'There’s no iQ address yet. Fill in step 1, save, then test again.' };
  }
  if (r.ok) return { ok: true, text: `Connected. iQ answered in ${secs} seconds.` };
  if (r.status === 401 || r.status === 403) {
    return {
      ok: false,
      text: `iQ answered, but didn’t accept our sign-in (${r.status}). Check the token in step 2, and the Cloudflare Access details if iQ sits behind it.`,
    };
  }
  if (r.status === 404) return { ok: false, text: 'iQ answered, but that address doesn’t exist (404). Check steps 1 and 3.' };
  if (r.status !== null) return { ok: false, text: `iQ answered with an error (${r.status}). Show the technical details for more.` };
  return { ok: false, text: 'iQ didn’t answer. Check the address in step 1, and that iQ is running.' };
}

/** Which forms and questions use each lookup list (by key). */
export function lookupUsage(
  forms: { id: string; name: string; fields: FieldDef[] }[],
): Map<string, { formId: string; formName: string; question: string }[]> {
  const out = new Map<string, { formId: string; formName: string; question: string }[]>();
  for (const form of forms) {
    for (const f of form.fields) {
      const key = f.optionsSource?.type === 'iq' ? f.optionsSource.key : null;
      if (!key) continue;
      const list = out.get(key) ?? [];
      list.push({ formId: form.id, formName: form.name, question: f.label });
      out.set(key, list);
    }
  }
  return out;
}
